import { instantAddUtc } from "@redotech/util/temporal";
import { Condition, type Parameters } from "./condition";
import {
  evaluateAgentTimePolicy,
  type AgentTimePolicyEvaluation,
  type AgentTimePolicyGraceWindows,
} from "./evaluate-agent-time-policy";

export type ReturnWindowCondition =
  | Condition.OrderWithin
  | Condition.FulfillmentPeriod
  | Condition.ShippingWithin;

export type ReturnWindowDates = Readonly<
  Partial<Pick<Parameters, "orderedOn" | "fulfilledOn" | "deliveredOn">>
>;

export type ReturnWindowBasis = "order" | "fulfillment" | "delivery";

export type AgentReturnWindowPolicyResult =
  | {
      kind: "evaluated";
      reason: "return_window";
      basis: ReturnWindowBasis;
      evaluation: AgentTimePolicyEvaluation;
    }
  | {
      kind: "missing_anchor";
      reason: "return_window";
      basis: ReturnWindowBasis;
    };

/** Callers select an eligible restriction and supply source dates, not flow fallbacks. */
export function evaluateAgentReturnWindowPolicy({
  condition,
  dates,
  now,
  graceWindows,
}: {
  condition: ReturnWindowCondition;
  dates: ReturnWindowDates;
  now: Temporal.Instant;
  graceWindows: AgentTimePolicyGraceWindows;
}): AgentReturnWindowPolicyResult {
  const { basis, anchor } = getReturnWindowAnchor(condition, dates);
  if (anchor === undefined) {
    return { kind: "missing_anchor", reason: "return_window", basis };
  }

  return {
    kind: "evaluated",
    reason: "return_window",
    basis,
    evaluation: evaluateAgentTimePolicy({
      now,
      policyDeadline: instantAddUtc(anchor, condition.duration),
      graceWindows,
    }),
  };
}

function getReturnWindowAnchor(
  condition: ReturnWindowCondition,
  dates: ReturnWindowDates,
): { basis: ReturnWindowBasis; anchor: Temporal.Instant | undefined } {
  switch (condition.type) {
    case Condition.ORDER_WITHIN:
      return { basis: "order", anchor: dates.orderedOn };
    case Condition.FULFILLMENT_WITHIN:
      return { basis: "fulfillment", anchor: dates.fulfilledOn };
    case Condition.DELIVERY_WITHIN:
      return { basis: "delivery", anchor: dates.deliveredOn };
  }
}
